import React, {useEffect, useState} from "react";
import {Table, Radio, Button, Empty, Pagination} from "antd";
import ProgressBar from "./ProgressBar";
import CampaignSelector from "./CampaignSelector";
import TableMobileCollapse from "./TableMobileCollapse";
import { CaretRightOutlined, CaretDownOutlined } from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import i18next from "i18next";

const table_columns = [
    {
        title: 'Campaigns',
        render: (record) => {
            return (<TableMobileCollapse record={record}/>)
        },
        width: '20%',
        className: 'mobile-column',
        responsive: ["xs", "sm", "md"],
    },
    {
        title: 'Campaigns',
        dataIndex: 'campaign',
        key: 'campaign',
        width: '10%',
        render: (text, record) => {
            return record.is_module
                ? {text}
                : <a href={''} onClick={(e) => {
                    e.preventDefault();
                }}>{text}</a>
        },
        responsive: ["lg"]
    },
    {
        title: 'Status',
        dataIndex: 'campaign_status',
        key: 'campaign_status',
        width: '10%',
        render: (text, record) => {
            return text ?? "Not Started";
        },
        responsive: ["lg"]
    },
    {
        title: 'Learners',
        dataIndex: 'allocated_users',
        key: 'allocated_users',
        width: '40%',
        sorter: (a, b) => a.allocated_users - b.allocated_users,
        responsive: ["lg"]
    },
    {
        title: () => {
            return (
                <div>
                    {i18next.t('custom_dashboard_training_details_columns_progress', 'Progress')}
                    <div className="progress-icons-container">
                        <div className="progress-icon">
                            <span className="icon-dot completed-icon-dot"> </span>
                            <span className="completed-icon-label">{i18next.t('custom_dashboard_training_details_columns_progress_cmpltd', 'Completed')}</span>
                        </div>
                        <div className="progress-icon">
                            <span className="icon-dot in-progress-icon-icon-dot"> </span>
                            <span className="in-progress-icon-icon-label">{i18next.t('custom_dashboard_training_details_columns_progress_in_prgrs', 'In Progress')}</span>
                        </div>
                        <div className="progress-icon">
                            <span className="icon-dot overdue-icon-icon-dot"> </span>
                            <span className="overdue-icon-icon-label">{i18next.t('custom_dashboard_training_details_columns_progress_overdue', 'Overdue')}</span>
                        </div>
                    </div>
                </div>
            );
        },
        key: 'progress',
        dataIndex: 'progress',
        width: '20%',
        render: (cell, row) => {
            return (<ProgressBar readings={row.progress}/>)
        },
        responsive: ["lg"]
    },
    {
        title: 'Campaign Date',
        dataIndex: 'campaign_date',
        key: 'campaign_date',
        render: (date) => {
            return <span>{date}</span>
        },
        responsive: ["lg"]
    }
];

const module_columns = [
    {
        title: 'Modules',
        dataIndex: 'campaign',
        key: 'campaign',
        width: '20%',
        responsive: ["lg"]
    },
    {
        title: 'Status',
        dataIndex: 'campaign_status',
        key: 'campaign_status',
        width: '10%',
        render: (value, record) => {
            return <span></span>
        },
        responsive: ["lg"]
    },
    {
        title: 'Learners',
        dataIndex: 'allocated_users',
        key: 'allocated_users',
        width: '10%',
        sorter: (a, b) => a.allocated_users - b.allocated_users,
        render: (allocated_users, record) => {
            return <span style={{visibility: 'hidden'}}>xxxxxxxxxxxxxxxx</span>
        },
        responsive: ["lg"]
    },
    {
        title: () => {
            return (
                <div>
                    {i18next.t('custom_dashboard_training_details_columns_progress', 'Progress')}
                    <div className="progress-icons-container">
                        <div className="progress-icon">
                            <span className="icon-dot completed-icon-dot"> </span>
                            <span className="completed-icon-label">{i18next.t('custom_dashboard_training_details_columns_progress_cmpltd', 'Completed')}</span>
                        </div>
                        <div className="progress-icon">
                            <span className="icon-dot in-progress-icon-icon-dot"> </span>
                            <span className="in-progress-icon-icon-label">{i18next.t('custom_dashboard_training_details_columns_progress_in_prgrs', 'In progress')}</span>
                        </div>
                        <div className="progress-icon">
                            <span className="icon-dot overdue-icon-icon-dot"> </span>
                            <span className="overdue-icon-icon-label">{i18next.t('custom_dashboard_training_details_columns_progress_overdue', 'Overdue')}</span>
                        </div>
                    </div>
                </div>
            );
        },
        key: 'progress',
        dataIndex: 'progress',
        width: '40%',
        render: (cell, row) => {
            return (<ProgressBar readings={row.progress}/>)
        },
        responsive: ["lg"]
    },
    {
        title: 'Campaign Date',
        dataIndex: 'date',
        key: 'date',
        width: '20%',
        render: (value) => {
            return <span></span>
        },
        responsive: ["lg"]
    }
];

const expandedRowRender = ({modules}) => {
    return <Table columns={module_columns} dataSource={modules || []} pagination={false} showHeader={false}/>;
};

const CampaignDetailsTable = ({sourceData}) => {
    const pageSize = 10;
    const campaignsData = sourceData.table_records.map(record => {
        record.modules = sourceData.module_records[record.key];
        return record;
    });
    const {t} = useTranslation();

    const [isLoading, setIsLoading] = useState(false);


    let locale = {
        emptyText: (
            <Empty
                image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                description={<p>{t('custom_dashboard_training_details_campaign_data_empty', 'No Training Campaign Data')}</p>}
            >
                <Button type="primary" href='/admin/campaign'>{t('custom_dashboard_training_details_create_campaign_btn', 'Create Campaign Now')}</Button>
            </Empty>
        )
    };

    return (
        <div id="campaign_table_container">
            <div id="campaign_table_header">
                <div className="table-selector-container">
                        <h4 className="campaign-detail-title">
                            {t('custom_dashboard_training_details_title', 'Training Campaign Details')}
                        </h4>
                </div>
            </div>
            <div id="campaign_table_main">
                <Table
                    className="campaign-table-content"
                    columns={table_columns}
                    dataSource={campaignsData}
                    pagination={{
                        defaultPageSize: pageSize,
                        position: ['topRight'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    loading={isLoading}
                    expandable={{
                        expandedRowRender: expandedRowRender,
                        columnWidth: '10px',
                        expandRowByClick: true,
                        module_columns: 'cursor-pointer',
                        expandIcon: ({ expanded, onExpand, record }) =>
                            expanded ? (
                                <CaretDownOutlined className='expand-icon' onClick={e => onExpand(record, e)} />
                            ) : (
                                <CaretRightOutlined className='collapse-icon' onClick={e => onExpand(record, e)} />
                            )
                    }}
                    locale={locale}
                />
            </div>
        </div>
    );
}

export default CampaignDetailsTable;
